import NavigationArrowIcon from "@/assets/icons/NavigationArrowIcon";
import CollectiveCanvasLogo from "@/assets/logos/CollectiveCanvasLogo";
import { FullscreenButton } from "@/components/common/FullscreenButton";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import { routes } from "@/routes/paths";
import { useIsMobile } from "@/ui";
import { secondaryFilledButtonProps } from "@canucci/base-mantine/theme";
import { Box, Button, Flex, Group, Text, Title, useMantineTheme } from "@mantine/core";
import { User } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import styles from "./CanvasHeader.module.css";

interface CanvasHeaderProps {
  title?: string;
  subtitle?: string;
  backAction?: {
    to: string;
    label?: string;
  };
  darkBackground?: boolean;
  availableLanguages?: string[];
  user?: User | null;
  hasBackButton?: boolean;
  showFullscreen?: boolean;
}

export function CanvasHeader({
  title,
  subtitle,
  backAction,
  darkBackground,
  availableLanguages,
  user,
  hasBackButton,
  showFullscreen = true,
}: CanvasHeaderProps) {
  const theme = useMantineTheme();
  const { spacing, semanticColors } = theme.other;
  const { t: tPages } = useTranslation("pages");
  const location = useLocation();
  const { canvasId } = useParams<{ canvasId: string }>();
  const isPreview = location.pathname.includes("--preview--");
  const isMobile = useIsMobile();
  const logoDestination = canvasId
    ? routes.canvas.overview.path(canvasId, location.pathname.startsWith("/participation/"))
    : "/";

  const showBackArrow = hasBackButton || !!backAction;

  return (
    <Box maw="100%" className={styles.header}>
      <Flex
        component="header"
        align="center"
        justify="space-between"
        px={isMobile ? "md" : "lg"}
        py={isMobile ? 0 : "sm"}
        c={theme.other.semanticColors.invertedText}
        className={`${styles.headerFlex} ${isMobile ? styles.headerFlexMobile : ""}`}
        style={{
          borderBottom: isMobile ? `1px solid ${semanticColors.borderMedium}` : "none",
        }}
      >
        {/* LEFT SECTION: Back Button or Language Switcher on Mobile */}
        <Flex className={`${styles.leftSection} ${isMobile ? styles.leftSectionMobile : styles.leftSectionDesktop}`}>
          {showBackArrow && backAction ? (
            <Button
              component={Link}
              to={backAction.to}
              variant="subtle"
              px={"xs"}
              ml={0}
              c={darkBackground ? theme.other.semanticColors.invertedText : theme.other.semanticColors.text}
              className={styles.backButton}
            >
              <Flex align="center" gap="xs">
                <NavigationArrowIcon
                  direction="left"
                  color={darkBackground ? theme.other.semanticColors.invertedText : theme.other.semanticColors.text}
                />
                {!isMobile && backAction.label && (
                  <Text
                    fz="sm"
                    fw="bold"
                    c={darkBackground ? theme.other.semanticColors.invertedText : theme.other.semanticColors.text}
                  >
                    {backAction.label}
                  </Text>
                )}
              </Flex>
            </Button>
          ) : isMobile && (availableLanguages?.length ?? 0) > 1 ? (
            <LanguageSwitcher availableLanguages={availableLanguages} />
          ) : null}
        </Flex>

        {/* CENTER SECTION: Title or Logo */}
        <Box className={`${styles.centerSection} ${isMobile ? styles.centerSectionMobile : ""}`}>
          <Title
            order={1}
            size="h4"
            className={styles.titleContainer}
            c={darkBackground ? semanticColors.invertedText : semanticColors.text}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title ?? (
              <Box className={`${styles.logoContainer} ${!isMobile ? styles.logoContainerDesktop : ""}`}>
                <Link to={logoDestination}>
                  <CollectiveCanvasLogo
                    color={darkBackground ? semanticColors.invertedText : semanticColors.text}
                    size="md"
                  />
                </Link>
              </Box>
            )}
          </Title>
        </Box>

        {/* RIGHT SECTION: Actions */}
        <Flex className={`${styles.rightSection} ${isMobile ? styles.rightSectionMobile : styles.rightSectionDesktop}`}>
          <Group gap="xs" align="center" mih="var(--mantine-other-spacing-desktopHeaderHeight)">
            {!isMobile && showFullscreen && <FullscreenButton />}
            {!isMobile && (availableLanguages?.length ?? 0) > 1 && (
              <LanguageSwitcher availableLanguages={availableLanguages} />
            )}
            {user && !isPreview && (
              <Button
                component={Link}
                to="/logout"
                state={{ from: location }}
                {...secondaryFilledButtonProps}
                size="sm"
                fz="sm"
                fw="bold"
              >
                {tPages("logout.logout")}
              </Button>
            )}
          </Group>
        </Flex>
      </Flex>

      {/* Subtitle Section */}
      {subtitle && (
        <Flex mt={!isMobile ? "md" : 0} px={isMobile ? "md" : 0} align="center" justify="space-between">
          {/* Spacer for desktop alignment */}
          <Box className={`${styles.subtitleSpacer} ${isMobile ? styles.subtitleSpacerMobile : ""}`} />

          <Title
            order={2}
            size="h4"
            fw="normal"
            c={darkBackground ? theme.other.semanticColors.invertedText : theme.other.semanticColors.text}
            mb={spacing.sectionGap}
            ml={!isMobile ? "xl" : 0}
            maw={!isMobile ? "calc(100% - 8rem)" : "100%"}
            className={`${styles.subtitleTitle} ${isMobile ? styles.subtitleTitleMobile : styles.subtitleTitleDesktop}`}
          >
            {subtitle}
          </Title>
          <Box className={`${styles.subtitleSpacer} ${isMobile ? styles.subtitleSpacerMobile : ""}`} />
        </Flex>
      )}
    </Box>
  );
}
